// Cart Notification Web Component
// Handles add-to-cart notification display and section rendering
// Depends on global trapFocus/removeTrapFocus from vendor/global.js

import { onVendorReady } from '@/utils/vendor-ready'

interface CartSection {
  id: string
  selector?: string
}

interface ParsedCartState {
  key: string
  sections: Record<string, string>
}

class CartNotification extends HTMLElement {
  private notification: HTMLElement
  private header: HTMLElement | null
  private onBodyClick: (evt: MouseEvent) => void
  // @ts-ignore - cartItemKey is set but not currently used (kept for future section rendering)
  private cartItemKey?: string
  private activeElement?: HTMLElement | null

  constructor() {
    super()

    this.notification = document.getElementById('cart-notification')!
    this.header = document.querySelector('sticky-header')
    this.onBodyClick = this.handleBodyClick.bind(this)

    this.notification.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close()
    })

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    )
  }

  open(): void {
    this.notification.classList.add('animate', 'active')

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus()
        if (typeof window.trapFocus === 'function') {
          window.trapFocus(this.notification)
        }
      },
      { once: true }
    )

    document.body.addEventListener('click', this.onBodyClick)
  }

  close(): void {
    this.notification.classList.remove('active')
    document.body.removeEventListener('click', this.onBodyClick)

    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(this.activeElement)
    }
  }

  renderContents(parsedState: ParsedCartState): void {
    this.cartItemKey = parsedState.key

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id)
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        )
      }
    })

    if (this.header && typeof (this.header as any).reveal === 'function') {
      ;(this.header as any).reveal()
    }
    // Note: Original had this.open() commented out
    // Uncomment if notification should auto-open on add to cart
    // this.open();
  }

  getSectionsToRender(): CartSection[] {
    return [
      // Product notification section (commented in original)
      // {
      //   id: 'cart-notification-product',
      //   selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      // },
      // Button notification (commented in original)
      // {
      //   id: 'cart-notification-button'
      // },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionInnerHTML(html: string, selector = '.shopify-section'): string {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)!.innerHTML
  }

  private handleBodyClick(evt: MouseEvent): void {
    const target = evt.target as HTMLElement
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu')
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null
      this.close()
    }
  }

  setActiveElement(element: HTMLElement | null): void {
    this.activeElement = element
  }
}

// Register custom element only after vendor is ready
// This ensures trapFocus/removeTrapFocus are available
onVendorReady(() => {
  if (!customElements.get('cart-notification')) {
    customElements.define('cart-notification', CartNotification)
  }
})

export default CartNotification
